/**
 * Web action permission resolver.
 *
 * Page permissions control navigation. Mapped business actions are controlled
 * only by the role action configuration. Buttons without a business action are
 * limited to explicit navigation and refresh controls.
 */

import { resolveRuntimePermissionPage } from '@/utils/tabPages'

const PRECLASS_ACTION_BY_NAME = {
  新增: 'create',
  整改: 'rectify',
  编辑: 'edit',
  修改: 'edit',
  查看: 'view',
  删除: 'delete',
  签到人数: 'sign',
  签到: 'sign',
  去签到: 'sign',
  批量签到: 'sign',
  输入签到人数: 'sign',
  查看影像: 'mediaView',
  培训影像: 'mediaEdit',
  搜索选择内容: 'selectDictionary',
  搜索选择教育内容: 'selectDictionary',
  搜索选择部位: 'selectDictionary',
  预览报表: 'view',
  提交: 'edit',
  保存: 'edit'
}

const PAGE_NAME_ALIASES = {
  attendance: 'attendanceStatistics',
  attendanceSet: 'attendanceSetting',
  examine: 'onlineExam',
  examLibrary: 'onlineExam',
  fields: 'systemFields',
  page: 'systemPage',
  pages: 'systemPage',
  person: 'personnel',
  report: 'dataReport',
  role: 'rights',
  salaryPay: 'salaryPayment',
  task: 'todoTakas',
  team: 'teamManagement'
}

const RUNTIME_PAGE_ALIASES = {
  organUser: 'user__机构用户',
  personUser: 'user__个人用户',
  orgAdmin: 'user__管理员列表',
  sysRoles: 'rights__系统角色',
  paper: 'onlineExam__试卷管理',
  paperQuestion: 'onlineExam__试卷管理',
  question: 'onlineExam__题库管理',
  publicPapers: 'onlineExam__公共试卷库',
  publicQuestions: 'onlineExam__公共试题库'
}

// These real page nodes reuse another page's configuration module. The
// component name therefore differs from the permission page that the user is
// currently operating under.
const CONTEXTUAL_PAGE_CODES = ['buildProject', 'projectSection']

const COMMON_CRUD = {
  查看: 'view',
  详情: 'view',
  明细: 'view',
  预览: 'view',
  二维码: 'view',
  披露二维码: 'view',
  监控: 'view',
  附件: 'view',
  结果: 'view',
  考卷: 'view',
  新增: 'create',
  新建: 'create',
  添加: 'create',
  新增下级: 'create',
  新增单位工程: 'create',
  新增顶级页面: 'create',
  创建: 'create',
  编制: 'create',
  编辑: 'edit',
  修改: 'edit',
  维护: 'edit',
  删除: 'delete',
  批量删除: 'delete',
  移除: 'edit',
  保存: 'edit',
  上传: 'edit',
  提交: 'edit',
  导入: 'import',
  导入清单: 'import',
  导入试题: 'import',
  导入考勤: 'import',
  导入薪酬: 'import',
  导入明细: 'import',
  下载导入模板: 'import',
  下载模板: 'import',
  导出Excel: 'exportExcel',
  导出Word: 'exportWord',
  打印报表: 'print',
  打印: 'print'
}

const BATCH_ACTION_BY_NAME = {
  批量退场: 'batchExit',
  批量封存: 'batchSeal',
  批量解封: 'batchUnseal',
  批量封禁: 'batchBan',
  批量解禁: 'batchUnban',
  批量新增: 'batchCreate',
  批量打卡: 'batchClock',
  批量缺勤打卡: 'batchAbsentClock',
  批量签到: 'batchSign',
  批量报备: 'batchSubmit',
  批量结算: 'batchSettle',
  批量删除: 'batchDelete',
  批量加标签: 'batchTag'
}

const PAGE_ACTION_BY_NAME = {
  aiPreClass: PRECLASS_ACTION_BY_NAME,
  preClassEducation: PRECLASS_ACTION_BY_NAME,
  billOfQuantities: {
    清单复核: 'review',
    保存: 'review',
    提交: 'edit'
  },
  attendanceStatistics: {
    打卡: 'clock',
    批量打卡: 'clock',
    批量缺勤打卡: 'clock',
    确认考勤: 'confirm',
    下载导入模板: 'import'
  },
  attendanceSetting: {
    上移: 'edit',
    下移: 'edit',
    添加点位及预览: 'edit',
    提交: 'edit'
  },
  salaryPayment: {
    结算: 'settle',
    确认: 'confirm',
    同意: 'confirm'
  },
  division: {
    复制: 'copy',
    挂接进度: 'structure',
    清单挂接: 'structure',
    复核工程量: 'quantityReview',
    工程结构数据: 'view',
    添加起讫桩号: 'edit',
    完善工程数量: 'edit',
    完善结构尺寸: 'edit',
    修改工程名称: 'edit',
    提交: 'edit'
  },
  waterwheelWork: {
    结束作业: 'finish'
  },
  training: {
    执行: 'execute',
    批量签到: 'sign',
    添加签到: 'sign',
    添加应到人员: 'edit',
    查看参会人员: 'view',
    查看考核试卷: 'view',
    查看培训资料: 'view',
    选择参会人员: 'edit',
    选择考核试卷: 'edit',
    选择培训资料: 'edit',
    添加试卷: 'edit',
    添加资料: 'edit',
    移除: 'edit',
    关联文件: 'edit',
    试题信息: 'view',
    更新: 'edit',
    提交: 'edit'
  },
  trainingMaterials: {
    公共资料库: 'publicLibrary',
    关联文件: 'edit',
    浏览量: 'view',
    预览或下载文件: 'view'
  },
  questionLibrary: {
    存疑试题审核: 'approve',
    试题信息: 'view'
  },
  onlineExam: {
    创建试题: 'edit',
    添加到试卷: 'edit',
    确认: 'confirm',
    公共试卷库: 'publicLibrary',
    公共试题库: 'publicLibrary',
    加标签: 'edit',
    审核通过: 'approve',
    重置难度: 'edit',
    添加题型: 'edit',
    移除: 'edit',
    试题信息: 'view',
    签到: 'sign',
    提交: 'edit'
  },
  onlineExam__试卷管理: {
    公共试卷库: 'publicLibrary',
    批量删除: 'batchDelete'
  },
  onlineExam__题库管理: {
    公共试题库: 'publicLibrary',
    重置难度: 'edit',
    加标签: 'edit',
    审核通过: 'approve',
    存疑试题审核: 'approve',
    批量删除: 'batchDelete',
    批量加标签: 'batchTag'
  },
  safetyInput: {
    计划编制: 'create',
    申报: 'submit',
    提交: 'submit',
    上报: 'submit',
    审核: 'approve',
    审批: 'approve',
    计划审批: 'approve',
    退回: 'reject',
    确认: 'confirm',
    保存: 'edit',
    编辑领用单: 'edit',
    分批: 'edit',
    计划台帐: 'view',
    使用台帐: 'view',
    领用单管理: 'view',
    入库登记: 'create',
    生成领用单: 'create',
    使用核查: 'edit',
    发票图片: 'view',
    其他附件: 'view',
    使用类型: 'view',
    使用明细: 'view',
    使用偏差分析: 'view',
    违规核查登记: 'edit',
    资金类型分配: 'edit'
  },
  materialManage: {
    入库登记: 'create',
    生成验收单: 'acceptance',
    生成领用单: 'requisition',
    领用单管理: 'view',
    保存: 'edit',
    更新验收明细: 'edit',
    更新领用明细: 'edit',
    编辑验收单: 'edit',
    编辑领用单: 'edit',
    分批: 'edit',
    导入明细: 'edit',
    下载导入模板: 'edit'
  },
  materialLibrary: {
    关联文件: 'edit',
    浏览量: 'view',
    预览或下载文件: 'view'
  },
  officialCar: {
    进场: 'entry',
    设备参数: 'params',
    参数指标: 'params',
    保险: 'insurance',
    保养: 'maintenance',
    维保: 'maintenance',
    保单图片: 'view',
    维修保养单据: 'view',
    运行监控状态: 'view',
    最近保养日期: 'view',
    最近检验日期: 'view',
    提交: 'edit'
  },
  ownEquipment: {
    设备参数: 'params',
    保险: 'insurance',
    保养: 'maintenance',
    维保: 'maintenance',
    保单图片: 'view',
    维修保养单据: 'view',
    最近保养日期: 'view',
    提交: 'edit'
  },
  mechanicalEquipment: {
    保单图片: 'view',
    维修保养单据: 'view',
    最近保养日期: 'view',
    保险到期日期: 'view',
    设备参数: 'view',
    提交: 'edit'
  },
  concealedWork: {
    验收: 'acceptance'
  },
  pouringOrder: {
    补方: 'supplement',
    审核: 'approve',
    拒绝: 'reject',
    中止: 'terminate',
    终止: 'terminate',
    签收: 'sign',
    '本单申请(m³)': 'view',
    查看历史申请: 'view',
    '累计签收(m³)': 'view',
    历史申请记录: 'view',
    提交: 'edit'
  },
  goodsShipment: {
    发车: 'depart',
    签收: 'sign',
    查看生产单: 'view',
    查看业务单: 'view',
    运输作业部位及内容: 'view',
    提交: 'edit'
  },
  mixingProduction: {
    发车: 'depart',
    处置: 'dispose',
    重置已生产量: 'reset',
    拌合站生产码: 'view',
    存草稿: 'edit',
    开始: 'edit',
    上移: 'edit',
    下移: 'edit',
    置顶: 'edit',
    自动排序: 'edit',
    刷新队列: 'edit',
    浇筑令编号: 'view',
    '生产进度(m³)': 'view',
    提交: 'edit'
  },
  notice: {
    发布: 'publish',
    存草稿: 'edit',
    提交: 'edit'
  },
  manageEvaluation: {
    兑换: 'exchange',
    提交: 'edit'
  },
  teamManagement: {
    解散: 'dissolve',
    关联人员: 'relatePeople',
    去添加人员: 'create',
    搜索选择作业内容: 'edit',
    提交: 'edit'
  },
  builder: {
    进场: 'entry',
    退场: 'exit',
    封存: 'seal',
    解封: 'unseal',
    查看个人档案: 'view',
    查看或完善个人档案: 'edit',
    弹窗提交: 'edit',
    去添加人员: 'create',
    添加新工种: 'create',
    添加新签名: 'edit',
    添加银行卡: 'edit',
    退场承诺书: 'view',
    完善档案工种: 'edit',
    新增个人档案: 'create',
    提交: 'edit'
  },
  contract: {
    提审: 'submit',
    报备: 'submit',
    批量报备: 'submit',
    本地上传: 'edit',
    电子签署: 'edit',
    添加明细: 'edit',
    新增发货单: 'create',
    新增结算单: 'create',
    新增开票记录: 'create',
    移除: 'edit',
    发货: 'edit',
    合同图片: 'view',
    同意: 'approve',
    审核: 'approve',
    拒绝: 'reject',
    驳回: 'reject',
    终止: 'terminate',
    附件: 'attachment'
  },
  contractor: {
    进场: 'entry',
    退场: 'exit',
    去办理退场: 'exit',
    查看个人档案: 'view',
    查看企业档案: 'view',
    查看未退场人员: 'view',
    弹窗提交: 'edit',
    去添加企业: 'create',
    去添加人员: 'create',
    去完善档案: 'edit',
    退场承诺书: 'view',
    提交: 'edit'
  },
  personnel: {
    封存: 'seal',
    解封: 'unseal',
    上传图片: 'edit',
    生成签名: 'edit',
    重新生成: 'edit'
  },
  factory: {
    关联人员: 'relate',
    关联机械: 'relate',
    关联劳务: 'relate'
  },
  bidSection: {
    标记默认标段: 'defaultBid'
  },
  project: {
    同步系统默认: 'syncDefault',
    重置为系统默认: 'syncDefault'
  },
  engineeringModel: {
    同步系统默认模型: 'syncDefault',
    同步系统默认: 'syncDefault',
    重置为系统默认: 'syncDefault'
  },
  systemEngineeringModel: {
    同步系统默认: 'syncDefault',
    重置为系统默认: 'syncDefault'
  },
  dataReport: {
    导出Excel: 'exportExcel',
    导出Word: 'exportWord',
    打印报表: 'print'
  },
  user: {
    封禁: 'ban',
    批量封禁: 'ban',
    解禁: 'unban',
    批量解禁: 'unban',
    配置角色: 'configureRole',
    密码初始化: 'resetPassword'
  },
  rights: {
    配置Web权限: 'configureWeb',
    配置App权限: 'configureApp',
    同步预设角色: 'syncPreset'
  },
  organization: {
    添加上级: 'linkParent',
    管理人员: 'view',
    机械设备: 'view',
    劳务公司: 'view',
    关联人员: 'edit',
    关联机械: 'edit',
    关联劳务: 'edit',
    同步系统默认: 'edit',
    重置为系统默认: 'edit',
    提交: 'edit'
  },
  systemFields: {
    同步系统默认: 'syncDefault',
    重置为系统默认: 'syncDefault',
    添加题型: 'edit'
  },
  systemPage: {
    上移: 'moveUp',
    下移: 'moveDown',
    提交: 'edit'
  },
  systemRuleConfig: {
    保存默认规则: 'saveDefault',
    保存机构规则: 'saveOrg',
    恢复继承: 'restoreInherit',
    恢复规则继承: 'restoreInherit'
  },
  todoTakas: {
    去办理: 'handle',
    去查看: 'view',
    查看: 'view'
  },
  progress: {
    编辑填报: 'edit',
    进度填报: 'create',
    重置: 'edit',
    提交: 'edit'
  }
}

const ACTION_BY_CODE = {
  add: 'create',
  edit: 'edit',
  delete: 'delete',
  check: 'view',
  preview: 'view',
  upload: 'edit',
  download: 'import',
  update: 'edit',
  save: 'edit',
  post: 'submit',
  confirm: 'confirm',
  approvePass: 'approve',
  approveReject: 'reject',
  toggle: 'edit',
  generate: 'edit',
  sync: 'edit',
  account: 'view',
  perfect: 'edit',
  qrcode: 'view',
  device: 'view',
  result: 'view',
  pushing: 'edit',
  sign: 'sign',
  rectify: 'rectify',
  print: 'print'
}

const GENERIC_NAME_RULES = [
  [/^配置Web权限$/i, 'configureWeb'],
  [/^配置App权限$/i, 'configureApp'],
  [/^配置角色$/, 'configureRole'],
  [/^密码初始化$/, 'resetPassword'],
  [/^(批量封禁|封禁)$/, 'ban'],
  [/^(批量解禁|解禁)$/, 'unban'],
  [/^标记默认标段$/, 'defaultBid'],
  [/^(同步系统默认|同步系统默认模型|重置为系统默认)$/, 'syncDefault'],
  [/^(导入|.*导入.*|下载.*模板)$/, 'import'],
  [/^导出Excel$/, 'exportExcel'],
  [/^导出Word$/, 'exportWord'],
  [/^(打印|打印报表)$/, 'print'],
  [/^(新增|新建|添加|新增下级|新增单位工程|新增顶级页面|创建.*|编制.*)$/, 'create'],
  [/^(编辑|修改|变更|维护.*|更新.*)$/, 'edit'],
  [/^(删除|批量删除)$/, 'delete'],
  [/^(查看|详情|明细|预览|二维码|披露二维码|监控|附件|结果|考卷|去查看)$/, 'view'],
  [/^(签到|批量签到|添加签到|签收)$/, 'sign'],
  [/^(打卡|批量打卡|批量缺勤打卡)$/, 'clock'],
  [/^(审核|审批|.*审核|.*审批|同意)$/, 'approve'],
  [/^(拒绝|驳回|退回)$/, 'reject'],
  [/^(提交|上报|申报|提审|报备|批量报备)$/, 'submit'],
  [/^(执行)$/, 'execute'],
  [/^(确认|确认考勤|确认薪酬)$/, 'confirm'],
  [/^(结算|薪酬结算)$/, 'settle'],
  [/^(发布)$/, 'publish'],
  [/^(验收|.*验收.*)$/, 'acceptance'],
  [/^(结束作业)$/, 'finish'],
  [/^(补方)$/, 'supplement'],
  [/^(发车)$/, 'depart'],
  [/^(中止|终止)$/, 'terminate'],
  [/^(进场)$/, 'entry'],
  [/^(退场)$/, 'exit'],
  [/^(封存)$/, 'seal'],
  [/^(解封)$/, 'unseal'],
  [/^(解散)$/, 'dissolve'],
  [/^(关联人员)$/, 'relatePeople'],
  [/^(关联机械|关联劳务)$/, 'relate'],
  [/^(设备参数)$/, 'params'],
  [/^(保险.*)$/, 'insurance'],
  [/^(保养|维保.*)$/, 'maintenance'],
  [/^(兑换)$/, 'exchange'],
  [/^(处置)$/, 'dispose'],
  [/^(重置已生产量)$/, 'reset']
]

function normalizedName(button) {
  const value =
    typeof button === 'string'
      ? button
      : (button && (button.name || button.buttonName || button.label)) || ''
  return String(value).replace(/\s+/g, '').trim()
}

function pageActionForName(pageActions, name) {
  if (!pageActions || !name) return ''
  if (pageActions[name]) return pageActions[name]
  const lowerName = name.toLowerCase()
  const matchedKey = Object.keys(pageActions).find(
    (key) => key.toLowerCase() === lowerName
  )
  return matchedKey ? pageActions[matchedKey] : ''
}

const PASSTHROUGH_BUTTON_CODES = new Set([
  'cancel',
  'back',
  'next',
  'return',
  'refresh',
  'expand'
])

const PASSTHROUGH_BUTTON_NAMES = new Set([
  '取消',
  '关闭',
  '返回',
  '返回列表',
  '上一步',
  '下一步',
  '刷新',
  '展开',
  '收起'
])

function isPassthroughButton(button) {
  const code = String((button && button.code) || '')
  const name = normalizedName(button)
  return (
    PASSTHROUGH_BUTTON_CODES.has(code) ||
    PASSTHROUGH_BUTTON_NAMES.has(name)
  )
}

function contextualBuilderAction(name, actionPage, state) {
  if (actionPage !== 'builder' && actionPage.indexOf('builder__') !== 0) {
    return ''
  }
  if (name !== '提交' && name !== '弹窗提交') return ''
  const pageStatus = String((state && state.pageStatus) || '')
  if (pageStatus === 'exit' || pageStatus === 'batch') return 'exitSubmit'
  if (pageStatus === 'add' || pageStatus === 'adminAdd') return 'entrySubmit'
  return ''
}

export function normalizeActionPageName(pageName, state) {
  const value = String(pageName || '')
  const contextPage =
    state && state.currentStep && state.currentStep.theme
      ? String(state.currentStep.theme)
      : ''
  const currentTab =
    state && state.currentTab ? String(state.currentTab.label || '') : ''
  const orgUid = state && state.user ? state.user.orgValue : ''
  const orgPages =
    state &&
    state.user &&
    Array.isArray(state.user.userPages)
      ? state.user.userPages.find((item) => item && item.orgUid === orgUid)
      : null
  const runtimePage = resolveRuntimePermissionPage(
    (orgPages && orgPages.pages) || [],
    contextPage,
    currentTab
  )
  if (runtimePage && runtimePage !== contextPage) {
    return runtimePage
  }
  if (CONTEXTUAL_PAGE_CODES.indexOf(contextPage) >= 0) {
    return contextPage
  }
  if (value === 'orgAllRoles') {
    const tabLabel =
      state &&
      state.currentTab &&
      String(state.currentTab.label || '')
    return tabLabel === '下级角色'
      ? 'rights__下级角色'
      : 'rights__机构角色'
  }
  if (RUNTIME_PAGE_ALIASES[value]) return RUNTIME_PAGE_ALIASES[value]
  const parent = value.indexOf('__') !== -1 ? value.split('__')[0] : value
  return PAGE_NAME_ALIASES[parent] || parent || value
}

export function resolveWebActionCode(pageName, button, state) {
  if (!pageName || !button) return ''
  const actionPage = normalizeActionPageName(pageName, state)
  if (button.actionCode) {
    return button.actionCode.indexOf('web:') === 0
      ? button.actionCode
      : `web:${actionPage}:${button.actionCode}`
  }
  const name = normalizedName(button)
  const profilePage =
    actionPage.indexOf('__') !== -1
      ? actionPage.split('__')[0]
      : actionPage
  const pageActions =
    PAGE_ACTION_BY_NAME[actionPage] ||
    PAGE_ACTION_BY_NAME[profilePage] ||
    {}
  let action =
    contextualBuilderAction(name, actionPage, state) ||
    BATCH_ACTION_BY_NAME[name] ||
    pageActionForName(pageActions, name) ||
    COMMON_CRUD[name] ||
    ''
  if (!action && name.indexOf('批量') === 0) {
    action = 'batchOther'
  }
  if (!action) {
    const matched = GENERIC_NAME_RULES.find(([pattern]) => pattern.test(name))
    action = matched ? matched[1] : ''
  }
  if (!action) action = ACTION_BY_CODE[button.code] || ''
  return action ? `web:${actionPage}:${action}` : ''
}

function capabilitiesForOrg(state) {
  const user = (state && state.user) || {}
  const orgUid = user.orgValue || ''
  const byOrg = user.webCapabilitiesByOrg || {}
  return {
    orgUid,
    capabilities: byOrg[orgUid]
  }
}

export function hasWebAction(state, actionCode) {
  if (!actionCode) return false
  const { capabilities } = capabilitiesForOrg(state)
  if (!capabilities || !Object.keys(capabilities).length) return false
  if (capabilities['*']) return true
  return !!capabilities[actionCode]
}

export function canUseWebAction(state, pageName, button) {
  if (!pageName) return false
  const actionCode = resolveWebActionCode(pageName, button, state)
  if (!actionCode) return isPassthroughButton(button)
  return hasWebAction(state, actionCode)
}

export function filterWebActionButtons(state, pageName, buttons) {
  if (!Array.isArray(buttons)) return []
  return buttons.filter(button => canUseWebAction(state, pageName, button))
}
